import { AbstractIntlMessages } from 'next-intl';
import mergeWith from 'lodash/mergeWith';
import { DEFAULT_LOCALE, SupportedLocale } from './config';

import enAdministration from './translations/en-US/administration.json';
import enAuth from './translations/en-US/auth.json';
import enCampaigns from './translations/en-US/campaigns.json';
import enCommon from './translations/en-US/common.json';
import enErrors from './translations/en-US/errors.json';
import enForms from './translations/en-US/forms.json';
import enGlofox from './translations/en-US/glofox.json';
import enIntegrations from './translations/en-US/integrations.json';
import enNavigation from './translations/en-US/navigation.json';
import enPages from './translations/en-US/pages.json';
import enRedirects from './translations/en-US/redirects.json';
import enReporting from './translations/en-US/reporting.json';
import enSiteTools from './translations/en-US/site-tools.json';
import enSites from './translations/en-US/sites.json';
import enTemplates from './translations/en-US/templates.json';
import enValidation from './translations/en-US/validation.json';

import esAdministration from './translations/es-ES/administration.json';
import esAuth from './translations/es-ES/auth.json';
import esCampaigns from './translations/es-ES/campaigns.json';
import esCommon from './translations/es-ES/common.json';
import esErrors from './translations/es-ES/errors.json';
import esForms from './translations/es-ES/forms.json';
import esGlofox from './translations/es-ES/glofox.json';
import esIntegrations from './translations/es-ES/integrations.json';
import esNavigation from './translations/es-ES/navigation.json';
import esPages from './translations/es-ES/pages.json';
import esRedirects from './translations/es-ES/redirects.json';
import esReporting from './translations/es-ES/reporting.json';
import esSiteTools from './translations/es-ES/site-tools.json';
import esSites from './translations/es-ES/sites.json';
import esTemplates from './translations/es-ES/templates.json';
import esValidation from './translations/es-ES/validation.json';

type Messages = AbstractIntlMessages;
type DomainMessages = Record<string, unknown>;

/**
 * Precisely typed en-US messages object (no widening annotation).
 * Used solely for deriving AppMessages — the type that powers next-intl
 * TypeScript augmentation (key autocomplete, locale typing).
 */
const EN_DEFAULT_MESSAGES = {
  common: enCommon,
  auth: enAuth,
  navigation: enNavigation,
  validation: enValidation,
  errors: enErrors,
  sites: enSites,
  pages: enPages,
  forms: enForms,
  integrations: enIntegrations,
  administration: enAdministration,
  campaigns: enCampaigns,
  reporting: enReporting,
  glofox: enGlofox,
  redirects: enRedirects,
  'site-tools': enSiteTools,
  templates: enTemplates,
};

export type AppMessages = typeof EN_DEFAULT_MESSAGES;

export const DOMAIN_FILES = [
  'common',
  'auth',
  'navigation',
  'validation',
  'errors',
  'sites',
  'pages',
  'forms',
  'glofox',
  'integrations',
  'administration',
  'campaigns',
  'reporting',
  'glofox',
  'redirects',
  'site-tools',
  'templates',
] as const;

type DomainFile = (typeof DOMAIN_FILES)[number];

/**
 * Static, eagerly imported message map for every locale/domain file.
 * Make sure to add new domain files here when adding translations.
 */
const MESSAGES: Record<SupportedLocale, Record<DomainFile, DomainMessages>> = {
  [SupportedLocale.EnUs]: EN_DEFAULT_MESSAGES,
  [SupportedLocale.EsEs]: {
    common: esCommon,
    auth: esAuth,
    navigation: esNavigation,
    validation: esValidation,
    errors: esErrors,
    sites: esSites,
    pages: esPages,
    forms: esForms,
    integrations: esIntegrations,
    administration: esAdministration,
    campaigns: esCampaigns,
    reporting: esReporting,
    glofox: esGlofox,
    redirects: esRedirects,
    'site-tools': esSiteTools,
    templates: esTemplates,
  },
};

/**
 * Custom merge for lodash.mergeWith that allows empty strings to override with fallbacks.
 * This will ensure
 * 1. Empty strings in the target locale will be overridden with fallback locale values
 * 2. Missing keys in the target locale will be populated with fallback locale values
 *
 * @param fallbackVal
 * @param overrideVal
 */
const mergeCustomizer = (fallbackVal: unknown, overrideVal: unknown): unknown => {
  if (
    typeof overrideVal === 'string' &&
    overrideVal === '' &&
    typeof fallbackVal === 'string' &&
    fallbackVal !== ''
  ) {
    return fallbackVal;
  }
  return undefined; // let lodash handle default deep merge
};

/**
 * Build the merged, namespaced messages object for a locale:
 * `{ common: {...}, auth: {...}, ... }`.
 */
export const getMessages = (locale: SupportedLocale): Messages => {
  const merged: Record<string, DomainMessages> = {};
  for (const domain of DOMAIN_FILES) {
    const domainMessages = MESSAGES[locale]?.[domain] ?? {};
    if (locale === DEFAULT_LOCALE) {
      merged[domain] = domainMessages;
    } else {
      const fallback = MESSAGES[DEFAULT_LOCALE][domain] ?? {};
      merged[domain] = mergeWith({}, fallback, domainMessages, mergeCustomizer);
    }
  }
  return merged as Messages;
};
